/* SIZESOFTX
=========================================================================================
* Descripción: Catálogo de Clientes
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 20/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import { Fragment, useEffect } from "react";
import { Box, Card, CardContent, Divider, Typography } from "@mui/material";


import SizeSoftXLayout from "../../../components/leyout/SizeSoftXLayout.jsx";
import MnuClientesActions from "../../../components/catalogos/clientes/MnuClientesActions.jsx";
import SizeSoftXModal from "../../../components/leyout/SizeSoftXModal.jsx";
import ModalBuscarClientes from "../../../components/herramientas/ModalBuscarClientes.jsx";
import { useClientes } from "../../../context/Catalogos/ClientesContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useFormTabLock } from "../../../hooks/useFormTabLock";

const getStructClientePayload = () => ({
    fecharegistro: { value: "", error: false, helper: "" },
    clavecliente: { value: "", error: false, helper: "" },
    razonsocial: { value: "", error: false, helper: "" },
    calle: { value: "", error: false, helper: "" },
    numeroexterior: { value: "", error: false, helper: "" },
    numerointerior: { value: "", error: false, helper: "" },
    coloniapoblacion: { value: "", error: false, helper: "" },
    delegacionmunicipio: { value: "", error: false, helper: "" },
    ciudadpais: { value: "", error: false, helper: "" },
    cp: { value: "", error: false, helper: "" },
    telefono1: { value: "", error: false, helper: "" },
    rfc: { value: "", error: false, helper: "" },
    email: { value: "", error: false, helper: "" },
    contacto: { value: "", error: false, helper: "" },
    condicionespago: { value: "", error: false, helper: "" },
    porcentajedescuento: { value: 0.05, error: false, helper: "" },
    colorcredencial: { value: "", error: false, helper: "" },
});

export default function FrmClientes() {
    const { isLocked: isFormLocked } = useFormTabLock('clientes', {
        title: 'Clientes',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Clientes'} ya está abierto en otra pestaña`);
        }
    });

    const {
        clientePayLoad,
        statusEnableFields,
        statusOpenModalEliminarCliente,
        setStatusOpenModalEliminarCliente,
        statusOpenModalBuscarCliente,
        setStatusOpenModalBuscarCliente,
        handleChangeDataClientePayload,
        handleObtenerClientesList,
        handleClickEliminarCliente,
        handleClickRegresarMainDashboard,
        handleOpenModalBuscarCliente,
        handleCloseModalBuscarCliente,
        handleClienteSeleccionadoFromModal,
    } = useClientes();

    const { handleChangeTitle, handleChangeClavePaginaActual } = useLayout();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (isFormLocked) {
            alert("El formulario de Clientes ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
            setTimeout(() => {
                handleClickRegresarMainDashboard();
            }, 900);
        }
    }, [isFormLocked]);

    useEffect(() => {
        handleObtenerClientesList();
        handleChangeTitle("Catálogo de Clientes");
        handleChangeClavePaginaActual("Clientes");
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.altKey && e.key.toLowerCase() === 'f1') {
                e.preventDefault();
                handleOpenModalBuscarCliente();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleOpenModalBuscarCliente]);

    return (
        <Fragment>
            <SizeSoftXLayout
                titlePage="Catálogo de Clientes"
                buttonActions={<MnuClientesActions />}
            >
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main" }}>
                                        Identificacion del cliente
                                    </Typography>
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
                                    <TextField autoComplete="off" name="fecharegistro" label="Fecha registro" value={clientePayLoad.fecharegistro?.value ?? ""} disabled fullWidth size="small" />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
                                    <TextField autoComplete="off" name="clavecliente" label="Clave cliente" value={clientePayLoad.clavecliente?.value ?? ""} disabled fullWidth size="small" />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
                                    <TextField autoComplete="off"
                                        name="razonsocial"
                                        label="Razon social"
                                        value={clientePayLoad.razonsocial?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.razonsocial?.error}
                                        helperText={clientePayLoad?.razonsocial?.helper}
                                        inputProps={{ maxLength: 120 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 4" } }}>
                                    <TextField autoComplete="off"
                                        name="contacto"
                                        label="Contacto"
                                        value={clientePayLoad.contacto?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        required
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.contacto?.error}
                                        helperText={clientePayLoad?.contacto?.helper}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 4" } }}>
                                    <TextField autoComplete="off"
                                        name="telefono1"
                                        label="Telefono"
                                        value={clientePayLoad.telefono1?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.telefono1?.error}
                                        helperText={clientePayLoad?.telefono1?.helper}
                                        inputProps={{ maxLength: 20 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 4" } }}>
                                    <TextField autoComplete="off"
                                        name="email"
                                        label="Email"
                                        value={clientePayLoad.email?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.email?.error}
                                        helperText={clientePayLoad?.email?.helper}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>

                                <Box sx={{ gridColumn: "1 / -1", mt: 1 }}>
                                    <Divider />
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main" }}>
                                        Domicilio
                                    </Typography>
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 6" } }}>
                                    <TextField autoComplete="off"
                                        name="calle"
                                        label="Calle"
                                        value={clientePayLoad.calle?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.calle?.error}
                                        helperText={clientePayLoad?.calle?.helper}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 6", md: "span 3" } }}>
                                    <TextField autoComplete="off"
                                        name="numeroexterior"
                                        label="No. exterior"
                                        value={clientePayLoad.numeroexterior?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.numeroexterior?.error}
                                        helperText={clientePayLoad?.numeroexterior?.helper}
                                        inputProps={{ maxLength: 20 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 6", md: "span 3" } }}>
                                    <TextField autoComplete="off"
                                        name="numerointerior"
                                        label="No. interior"
                                        value={clientePayLoad.numerointerior?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.numerointerior?.error}
                                        helperText={clientePayLoad?.numerointerior?.helper}
                                        inputProps={{ maxLength: 20 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 4" } }}>
                                    <TextField autoComplete="off"
                                        name="coloniapoblacion"
                                        label="Colonia / poblacion"
                                        value={clientePayLoad.coloniapoblacion?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.coloniapoblacion?.error}
                                        helperText={clientePayLoad?.coloniapoblacion?.helper}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 4" } }}>
                                    <TextField autoComplete="off"
                                        name="delegacionmunicipio"
                                        label="Delegacion / municipio"
                                        value={clientePayLoad.delegacionmunicipio?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.delegacionmunicipio?.error}
                                        helperText={clientePayLoad?.delegacionmunicipio?.helper}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 8", md: "span 3" } }}>
                                    <TextField autoComplete="off"
                                        name="ciudadpais"
                                        label="Ciudad / pais"
                                        value={clientePayLoad.ciudadpais?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.ciudadpais?.error}
                                        helperText={clientePayLoad?.ciudadpais?.helper}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 4", md: "span 1" } }}>
                                    <TextField autoComplete="off"
                                        name="cp"
                                        label="CP"
                                        value={clientePayLoad.cp?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.cp?.error}
                                        helperText={clientePayLoad?.cp?.helper}
                                        inputProps={{ maxLength: 10 }}
                                    />
                                </Box>

                                <Box sx={{ gridColumn: "1 / -1", mt: 1 }}>
                                    <Divider />
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main" }}>
                                        Datos fiscales y comerciales
                                    </Typography>
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
                                    <TextField autoComplete="off"
                                        name="rfc"
                                        label="RFC"
                                        value={clientePayLoad.rfc?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.rfc?.error}
                                        helperText={clientePayLoad?.rfc?.helper}
                                        inputProps={{ maxLength: 15 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 12", md: "span 5" } }}>
                                    <TextField autoComplete="off"
                                        name="condicionespago"
                                        label="Condiciones de pago"
                                        value={clientePayLoad.condicionespago?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.condicionespago?.error}
                                        helperText={clientePayLoad?.condicionespago?.helper}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 6", md: "span 2" } }}>
                                    <TextField autoComplete="off"
                                        name="porcentajedescuento"
                                        label="% descuento"
                                        value={clientePayLoad.porcentajedescuento?.value ?? 0}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        type="text"
                                        inputMode="numeric"
                                        enterKeyHint="enter"
                                        error={clientePayLoad?.porcentajedescuento?.error}
                                        helperText={clientePayLoad?.porcentajedescuento?.helper}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { xs: "span 6", md: "span 2" } }}>
                                    <TextField autoComplete="off"
                                        name="colorcredencial"
                                        label="Color credencial"
                                        value={clientePayLoad.colorcredencial?.value ?? ""}
                                        onChange={handleChangeDataClientePayload}
                                        disabled={statusEnableFields}
                                        fullWidth
                                        size="small"
                                        error={clientePayLoad?.colorcredencial?.error}
                                        helperText={clientePayLoad?.colorcredencial?.helper}
                                        inputProps={{ maxLength: 30 }}
                                    />
                                </Box>
                            </Box>                        </CardContent>
                    </Card>
                </Box>
            </SizeSoftXLayout>

            <SizeSoftXModal
                open={statusOpenModalEliminarCliente}
                title={"¿Está seguro de querer eliminar el cliente? Esta acción no se puede deshacer."}
                onClose={() => setStatusOpenModalEliminarCliente(false)}
                onSuccess={handleClickEliminarCliente}
                success="Eliminar"
            />

            <ModalBuscarClientes
                openModal={statusOpenModalBuscarCliente}
                handleCloseModal={handleCloseModalBuscarCliente}
                onClienteSelect={handleClienteSeleccionadoFromModal}
            />
        </Fragment>
    );
}






